import { Link } from "react-router-dom";
import { ArrowUpRight, Target, LineChart, Users, Layers } from "lucide-react";
import PageHero from "../components/sections/PageHero";

const services = [
  { icon: Target, title: "Embedded Growth Pods", desc: "We sit inside your org for 90 days, own the funnel, ship the experiments." },
  { icon: LineChart, title: "Activation & Retention Audits", desc: "Forensic review of your funnel — followed by an executable 60-day playbook." },
  { icon: Layers, title: "GTM & Positioning", desc: "Re-architect your category narrative, ICP, and message-market fit with us as co-builders." },
  { icon: Users, title: "Founder-Office Team", desc: "Senior team on demand — for the project that doesn't justify a full-time hire yet." },
];

const phases = [
  { n: "01", t: "Diagnose", d: "Two-week embed. Customer interviews, funnel forensics, internal interviews. We come back with the real problem — usually not the one you briefed us on." },
  { n: "02", t: "Decide", d: "A single-page operating thesis. The bet, the metric, the timeline. Signed off jointly. From here on, the number is shared." },
  { n: "03", t: "Deploy", d: "We sit inside your tools, your stand-ups, your Slack. We don't write decks — we ship experiments." },
  { n: "04", t: "Hand-over", d: "We leave behind a documented, hireable system — and a shortlist of the people who can run it." },
];

const GrowthConsulting = () => (
  <div data-testid="growth-page">
    <PageHero
      kicker="/ Growth Consulting"
      title="We don't write decks."
      accent="We ship the number."
      breadcrumb={["Growth Consulting"]}
      lead="A 90-day embedded engagement model for mid-market and venture-backed teams. We co-own the metric, sit inside the org, and leave behind a system — not a slide deck."
    />

    <section className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-ink">
        {services.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.title} data-testid={`growth-service-${i}`} className="border-r border-b border-ink p-10 md:p-14 group hover:bg-[#F3F3F1] transition-colors">
              <div className="flex items-start justify-between mb-10">
                <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A]">/ 0{i + 1}</p>
                <Icon className="w-6 h-6 text-ink-muted group-hover:text-[#9F381A] transition-colors" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4">{s.title}</h3>
              <p className="text-base text-ink-muted leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </section>

    <section data-testid="growth-process" className="bg-[#F3F3F1] py-24 md:py-32">
      <div className="container-wide">
        <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ The 90-day operating model</p>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-16 max-w-3xl">
          Four phases.<br />
          <em className="font-editorial italic text-[#9F381A]">One shared metric.</em>
        </h2>
        <div className="space-y-0 border-t border-ink">
          {phases.map((p) => (
            <div key={p.n} data-testid={`growth-phase-${p.n}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-12 border-b border-ink group hover:bg-white transition-colors px-2">
              <div className="md:col-span-2">
                <span className="font-heading text-5xl md:text-6xl tracking-tighter text-[#9F381A] group-hover:translate-x-1 transition-transform inline-block">{p.n}</span>
              </div>
              <div className="md:col-span-3">
                <h3 className="font-heading text-2xl md:text-3xl tracking-tight">{p.t}</h3>
              </div>
              <div className="md:col-span-7">
                <p className="text-lg text-ink-muted leading-relaxed">{p.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="container-wide py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <img src="https://images.pexels.com/photos/28589260/pexels-photo-28589260.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" alt="Growth Consulting" loading="lazy" decoding="async" className="w-full h-[420px] md:h-[560px] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
      </div>
      <div className="lg:col-span-5">
        <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ Who this is for</p>
        <h2 className="font-heading text-3xl md:text-4xl tracking-tighter leading-[0.95] mb-8">
          Mid-market teams between<br />
          <em className="font-editorial italic text-[#9F381A]">$2M and $30M ARR</em><br />
          who refuse to outsource their thinking.
        </h2>
        <Link to="/contact" data-testid="growth-cta" className="inline-flex items-center gap-3 bg-ink text-white px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors">
          Brief us in 30 minutes <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  </div>
);

export default GrowthConsulting;
